import { Component, Input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';

import { AdminPermitDetail, PermitInternalNote, PopulatedUser } from '../../models/permit.model';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { UserService } from '../../../user/service/user.abstract.service';
import { ProfileResponse } from '../../../user/user.model';

interface MentionCandidate {
    id: string;
    name: string;
    email: string;
    picture: string;
    initials: string;
}

// Where in a textarea's value the "@query" currently being typed sits, so a selected
// mention can replace exactly that span.
interface MentionRange {
    start: number;
    end: number;
}

@Component({
    selector: 'app-permit-internal-note',
    templateUrl: './permit-internal-note.component.html',
    styleUrls: ['./permit-internal-note.component.scss'],
})
export class PermitInternalNoteComponent implements OnInit, OnChanges, OnDestroy {
    @Input() permit: AdminPermitDetail;
    @Output() permitUpdated = new EventEmitter<void>();

    // Threaded internal notes (maps to PSD "Internal Comments").
    notes: PermitInternalNote[] = [];

    // Add new note
    newNoteText = '';
    isAdding = false;

    // Inline edit of an existing note
    editingNoteId: string | null = null;
    editText = '';
    isSavingEdit = false;

    // @mention search, shared between the new-note textarea and the edit textarea.
    private readonly MIN_MENTION_QUERY_LENGTH = 1;
    showMentionDropdown = false;
    mentionLoading = false;
    mentionSearchResults: MentionCandidate[] = [];
    private mentionSearchSubject = new Subject<string>();
    mentionTarget: 'new' | 'edit' | null = null;
    private mentionRange: MentionRange | null = null;
    private activeTextarea: HTMLTextAreaElement | null = null;

    // Author name/picture: `author` often arrives as a bare user id rather than populated
    // — resolve it the same way permit-info.component.ts resolves the applicant's profile.
    private authorProfiles = new Map<string, ProfileResponse>();

    // A plain <textarea> can't render an inline chip, so while composing/editing we show a
    // friendly "@Name" and keep the name→id mapping here — the raw "@[id:name]" wire token
    // is only reconstructed right before sending, in applyMentionTokens().
    private mentionNameToId = new Map<string, string>();

    private destroy$ = new Subject<void>();

    constructor(
        private permitService: PermitV2Service,
        private translate: TranslateService,
        private messageService: MessageService,
        private userService: UserService,
        private router: Router,
        private sanitizer: DomSanitizer,
    ) {}

    ngOnInit(): void {
        this.mentionSearchSubject.pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((query) => this.searchMentionAdmins(query));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.permit) {
            this.notes = this.permit?.internalNotes ? [...this.permit.internalNotes] : [];
            this.resolveAuthorProfiles();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ─── Add note ──────────────────────────────────────────────────────────────

    addNote(): void {
        const text = this.applyMentionTokens((this.newNoteText || '').trim());
        if (!text || this.isAdding) return;

        this.isAdding = true;
        this.permitService
            .addInternalNote(this.permit._id, { description: text })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.newNoteText = '';
                    this.isAdding = false;
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.isAdding = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    onNoteEnter(event: KeyboardEvent): void {
        if (event.shiftKey) return;
        event.preventDefault();
        this.addNote();
    }

    // ─── Edit note ─────────────────────────────────────────────────────────────

    startEdit(note: PermitInternalNote): void {
        this.editingNoteId = note._id;
        this.editText = this.tokensToDisplay(note.text);
        if (this.permit?.externalId) {
            this.messageService.warning(this.translate.instant('permit.manage.collaboration.internal_notes_external_edit_warning'));
        }
    }

    cancelEdit(): void {
        this.editingNoteId = null;
        this.editText = '';
    }

    saveEdit(note: PermitInternalNote): void {
        const text = this.applyMentionTokens((this.editText || '').trim());
        if (!text || this.isSavingEdit) return;

        this.isSavingEdit = true;
        this.permitService
            .editInternalNote(this.permit._id, note._id, { description: text })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isSavingEdit = false;
                    this.editingNoteId = null;
                    this.editText = '';
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.isSavingEdit = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    // ─── @mentions ─────────────────────────────────────────────────────────────
    // Typing "@word" in either textarea opens a debounced admin search. Picking a result
    // inserts an `@[userId:Name]` token into the plain text (same wire format request-v2
    // uses) — PermitMentionPipe renders that token as a clickable "@Name" pill wherever
    // the note is displayed.

    onNoteInput(event: Event): void {
        this.detectMention(event.target as HTMLTextAreaElement, 'new');
    }

    onEditInput(event: Event): void {
        this.detectMention(event.target as HTMLTextAreaElement, 'edit');
    }

    onTextareaBlur(): void {
        // Delay so a click on a dropdown row (mousedown → blur → click) still lands before we close it.
        setTimeout(() => this.closeMentionDropdown(), 150);
    }

    private detectMention(textarea: HTMLTextAreaElement, target: 'new' | 'edit'): void {
        const cursor = textarea.selectionStart ?? textarea.value.length;
        const textBeforeCursor = textarea.value.substring(0, cursor);
        const match = textBeforeCursor.match(/(^|[\s])@(\w*)$/);

        if (!match || match[2].length < this.MIN_MENTION_QUERY_LENGTH) {
            this.closeMentionDropdown();
            return;
        }

        this.activeTextarea = textarea;
        this.mentionTarget = target;
        this.mentionRange = { start: cursor - match[2].length - 1, end: cursor };
        this.showMentionDropdown = true;
        this.mentionSearchSubject.next(match[2]);
    }

    private searchMentionAdmins(query: string): void {
        this.mentionLoading = true;
        this.userService
            .searchAdmins(query, 8)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (admins: any[]) => {
                    this.mentionLoading = false;
                    this.mentionSearchResults = (admins || []).map((admin) => {
                        const name = admin.name || `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
                        return {
                            id: admin.id || admin._id,
                            name,
                            email: admin.email || '',
                            picture: admin.picture || '',
                            initials: this.initialsFromName(name),
                        };
                    });
                },
                error: () => {
                    this.mentionLoading = false;
                    this.mentionSearchResults = [];
                },
            });
    }

    selectMention(admin: MentionCandidate): void {
        if (!this.activeTextarea || !this.mentionRange) return;

        this.mentionNameToId.set(admin.name, admin.id);

        const value = this.activeTextarea.value;
        const before = value.substring(0, this.mentionRange.start);
        const after = value.substring(this.mentionRange.end);
        const token = `@${admin.name} `;
        const newValue = before + token + after;

        if (this.mentionTarget === 'edit') {
            this.editText = newValue;
        } else {
            this.newNoteText = newValue;
        }

        const textarea = this.activeTextarea;
        const cursorPos = before.length + token.length;
        this.closeMentionDropdown();

        // Wait for Angular to flush the ngModel-bound value back into the textarea before
        // moving the cursor, otherwise the browser's stale value overwrites the new one.
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(cursorPos, cursorPos);
        });
    }

    closeMentionDropdown(): void {
        this.showMentionDropdown = false;
        this.mentionSearchResults = [];
        this.activeTextarea = null;
        this.mentionRange = null;
        this.mentionTarget = null;
    }

    onMentionClick(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLElement) || !target.classList.contains('mention')) return;
        const userId = target.dataset.userId;
        if (!userId) return;
        const url = this.router.serializeUrl(this.router.createUrlTree(['/user-update', userId]));
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    private initialsFromName(name: string): string {
        const parts = (name || '').trim().split(' ').filter(Boolean);
        if (!parts.length) return '';
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    /** "@[id:name]" wire tokens → friendly "@name" for display in the plain-text textarea. */
    private tokensToDisplay(text: string): string {
        return (text || '').replace(/@\[([^\]:]+):([^\]]+)\]/g, (_match, id, name) => {
            if (name === '__deleted__') return this.translate.currentLang === 'fr' ? '@Utilisateur supprimé' : '@Deleted user';
            this.mentionNameToId.set(name, id);
            return `@${name}`;
        });
    }

    /** Friendly "@name" text → "@[id:name]" wire tokens, right before sending to the backend. */
    private applyMentionTokens(text: string): string {
        let result = text || '';
        // Longest names first, so "John" can't clobber part of an already-replaced "John Smith".
        const names = Array.from(this.mentionNameToId.keys()).sort((a, b) => b.length - a.length);
        for (const name of names) {
            const id = this.mentionNameToId.get(name);
            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            result = result.replace(new RegExp(`@${escaped}(?!\\w)`, 'g'), `@[${id}:${name}]`);
        }
        return result;
    }

    /**
     * A native <textarea> can't style a substring, so the mention "pill" while composing is a
     * layering trick: this renders the SAME text as HTML (with mentions wrapped) into a div
     * sitting behind the textarea; the textarea's own text is made transparent (see SCSS) so
     * only this div's colored version shows through, while the textarea still handles all real
     * typing/selection/cursor.
     */
    highlightMentions(text: string): SafeHtml {
        const value = text || '';
        let escaped = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const names = Array.from(this.mentionNameToId.keys()).sort((a, b) => b.length - a.length);
        for (const name of names) {
            const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            escaped = escaped.replace(new RegExp(`@${escapedName}(?!\\w)`, 'g'), (match) => `<span class="mention-inline">${match}</span>`);
        }
        // A trailing newline needs a non-empty last line, or the overlay's box collapses shorter
        // than the textarea's and the two scroll out of sync.
        if (value.endsWith('\n')) escaped += '&nbsp;';
        return this.sanitizer.bypassSecurityTrustHtml(escaped);
    }

    /** Keeps the highlight overlay scrolled to the same position as the textarea on top of it. */
    onTextareaScroll(event: Event, overlay: HTMLElement): void {
        const textarea = event.target as HTMLTextAreaElement;
        overlay.scrollTop = textarea.scrollTop;
        overlay.scrollLeft = textarea.scrollLeft;
    }

    // ─── Display helpers ──────────────────────────────────────────────────────

    get currentLang(): string {
        return this.translate.currentLang;
    }

    private authorId(author: string | PopulatedUser): string {
        if (!author) return '';
        return typeof author === 'string' ? author : author._id;
    }

    private resolveAuthorProfiles(): void {
        const idsToFetch = new Set<string>();
        for (const note of this.notes) {
            const id = this.authorId(note.author);
            if (id && !this.authorProfiles.has(id)) idsToFetch.add(id);
        }
        idsToFetch.forEach((id) => {
            this.userService
                .getProfileById(id)
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe({
                    next: (profile) => {
                        this.authorProfiles.set(id, profile);
                    },
                    // Leave unresolved — display helpers fall back to whatever's embedded, or blank.
                    error: () => {},
                });
        });
    }

    getAuthorName(author: string | PopulatedUser): string {
        const profile = this.authorProfiles.get(this.authorId(author));
        if (profile) return `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
        if (author && typeof author !== 'string') return `${author.firstName} ${author.lastName}`.trim();
        return '';
    }

    getAuthorInitials(author: string | PopulatedUser): string {
        const profile = this.authorProfiles.get(this.authorId(author));
        const firstName = profile?.firstName || (author && typeof author !== 'string' ? author.firstName : '') || '';
        const lastName = profile?.lastName || (author && typeof author !== 'string' ? author.lastName : '') || '';
        return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    }

    getAuthorPicture(author: string | PopulatedUser): string {
        return this.authorProfiles.get(this.authorId(author))?.picture || '';
    }

    getRelativeTime(dateStr: string): string {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const diffMs = Date.now() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return this.translate.instant('permit.manage.collaboration.just_now');

        const locale = this.translate.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        const diffH = Math.floor(diffMin / 60);
        const diffD = Math.floor(diffH / 24);
        if (diffD >= 7) return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });

        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        if (diffMin < 60) return rtf.format(-diffMin, 'minute');
        if (diffH < 24) return rtf.format(-diffH, 'hour');
        return rtf.format(-diffD, 'day');
    }
}
